import * as React from "react";
import useOnclickOutside from "react-cool-onclickoutside";
import Link from "next/link";
import { useMachine } from "@xstate/react";
import {
  IAppointment,
  IReservationMode,
} from "@nju/data-access-portal-appointment";
import { isFtthOffer, useOffer } from "@nju/data-access-portal-offers";
import { IFtthOrchestrator } from "@nju/data-access-portal-orchestrator";
import { pushAddShippingInfoEventGA4, pushSimpleEvent } from "@nju/data-layer";
import { redirectToOrchestratorState } from "@nju/feature-portal-orchestrator";
import { CancelOrderModal } from "@nju/feature-sales-order";
import {
  ErrorMessage,
  Input,
  MobilePhoneNumberField,
  REPLACE_SPACES,
  TextArea,
} from "@nju/form";
import { Icon } from "@nju/icons";
import { portalRoutes, salesRoutes } from "@nju/routes";
import { IScope } from "@nju/scope";
import {
  A,
  Button,
  ButtonsWrapper,
  Heading,
  Modal,
  Text,
  Tooltip,
  useOverlay,
} from "@nju/ui";
import { AppointmentDateChangeSuccess } from "./AppointmentDateChangeSuccess";
import { AppointmentDateChangeUnavailable } from "./AppointmentDateChangeUnavailable";
import { appointmentFormMachine } from "./appointmentForm.machine";
import { Calendar } from "./Calendar";

interface IAppointmentFormPropsProps {
  mode: IReservationMode;
  onSubmit(state?: IFtthOrchestrator): void;
  ftthOrchestrator: IFtthOrchestrator;
  variant?: IScope;
}

export function AppointmentForm({
  mode,
  onSubmit,
  ftthOrchestrator,
  variant = "portal",
}: IAppointmentFormPropsProps) {
  const modal = useOverlay();
  const {
    id,
    appointmentTimeSlot,
    note: message,
    contactNumberWithoutCountyCode,
    state,
    offerIds,
  } = ftthOrchestrator;

  const offer = useOffer(offerIds[0]);

  const [{ context, matches }, send] = useMachine(appointmentFormMachine, {
    actions: {
      /**
       * There is no way to narrow this action to `form_sent`.
       */
      onSubmit: (_, event) => {
        if (event.type === "FORM_SENT") {
          onSubmit(event.state);
        }
      },
    },
    context: {
      orchestratorId: id,
      phone: contactNumberWithoutCountyCode || "",
      note: message,
      mode,
      selectedTimeSlot: {
        slot: appointmentTimeSlot as IAppointment["slot"],
      },
    },
  });

  const {
    appointmentValue,
    isCalendarVisible,
    phoneError,
    appointmentError,
    phone,
    note,
    selectedTimeSlot,
    formError,
  } = context;

  const ref = useOnclickOutside(() => {
    if (isCalendarVisible) {
      send({ type: "TOGGLE_CALENDAR" });
    }
  });

  const handleOnPhoneValueChange = React.useCallback(
    (value: string) => {
      send({
        type: "SET",
        name: "phone",
        value: value.replace(REPLACE_SPACES, ""),
      });
    },
    [send],
  );

  const handleOnToggleCalendar = React.useCallback(() => {
    send({ type: "TOGGLE_CALENDAR" });
  }, [send]);

  const handleOnSelectSlot = React.useCallback(
    (appointment: IAppointment) => {
      send({ type: "SET_TIME_SLOT", value: appointment });
      handleOnToggleCalendar();
    },
    [handleOnToggleCalendar, send],
  );

  const handleOnNoteValueChange = React.useCallback(
    (value: string) => {
      send({ type: "SET", name: "note", value });
    },
    [send],
  );

  const handleOnSubmit: React.FormEventHandler<HTMLFormElement> =
    React.useCallback(
      (event) => {
        if (isFtthOffer(offer)) {
          pushAddShippingInfoEventGA4({
            numberCount: 1,
            productId: offer.offerId,
            shippingTier: "Instalacja",
            itemCategory: "Internet stacjonarny",
            productPrice: offer.totalRecurringPrice,
            downloadSpeed: String(offer.downloadSpeed),
          });
        }

        event.preventDefault();
        send({ type: "SUBMIT" });
      },
      [send, offer],
    );

  const handlePushDataLayer = React.useCallback(() => {
    if (mode != "CHANGE_FULFILLMENT") {
      pushSimpleEvent({
        event: "activity_service",
        eventName: "process_finish_later",
      });
    }
  }, [mode]);

  const handleCancelAppointmentChange = React.useCallback(() => {
    redirectToOrchestratorState({ state, mode: "ftth" });
    pushSimpleEvent({
      event: "activity_service",
      eventName: "process_finish_later",
    });
  }, [state]);

  const handleCalendarError = React.useCallback(() => {
    send({ type: "CALENDAR_ERROR" });
  }, [send]);

  if (matches("appointmentSuccess") && mode === "CHANGE_FULFILLMENT") {
    return <AppointmentDateChangeSuccess onSubmit={onSubmit} />;
  }

  if (matches("appointmentUnavailable") && mode === "CHANGE_FULFILLMENT") {
    return <AppointmentDateChangeUnavailable onSubmit={onSubmit} />;
  }

  return (
    <>
      <Heading
        className="font-bold mb-5 from-tablet:mb-10"
        font="ttfirs"
        tone="neutral"
        variant="h2"
      >
        wybierz termin instalacji
      </Heading>
      <form onSubmit={handleOnSubmit}>
        <div className="max-w-[600px]">
          <div className="mb-5 relative" data-cy="installation-date-section">
            <div>
              <Input
                label="termin instalacji"
                name="calendar"
                onFocus={handleOnToggleCalendar}
                state={appointmentError ? "error" : undefined}
                suffix={
                  <div onClick={handleOnToggleCalendar} role="button">
                    <Icon height={29} name="calendar-check" width={29} />
                  </div>
                }
                value={appointmentValue}
              />
            </div>
            <div data-cy="error-message-installation-date">
              {appointmentError && <ErrorMessage message={appointmentError} />}
            </div>
            {isCalendarVisible && (
              <Calendar
                ftthOrchestrator={ftthOrchestrator}
                onCalendarError={handleCalendarError}
                onClose={handleOnToggleCalendar}
                onSlotSelect={handleOnSelectSlot}
                ref={ref}
                selectedDate={selectedTimeSlot}
                variant={variant}
              />
            )}
          </div>
          <MobilePhoneNumberField
            defaultValue={phone}
            isDisabled={mode === "CHANGE_FULFILLMENT"}
            onValueChange={handleOnPhoneValueChange}
            state={phoneError ? "error" : undefined}
            suffix={
              <Tooltip content="Podany numer będzie służył do kontaktu z Tobą w sprawie instalacji usługi oraz w celach obsługowych związanych z usługą, którą będziemy Ci świadczyć, np. informowania Cię o zbliżających się płatnościach. Po zakupie usługi możesz zmienić ten numer lub zrezygnować z kontaktu w powyższych sprawach.">
                <Icon height={29} name="info-circle" width={29} />
              </Tooltip>
            }
          />
          <div data-cy="error-message-phone-number">
            {phoneError && <ErrorMessage message={phoneError} />}
          </div>
          <div className="mt-2 flex">
            <Icon className="mr-2" height={20} name="info-circle" width={20} />
            <Text>Możesz podać numer komórkowy lub stacjonarny.</Text>
          </div>
          <TextArea
            defaultValue={note}
            isDisabled={mode === "CHANGE_FULFILLMENT"}
            label="wiadomość dla technika (opcjonalnie)"
            maxLength={100}
            name="note"
            onChange={handleOnNoteValueChange}
            placeholder={"wiadomość dla technika (opcjonalnie)"}
            rows={5}
          />
        </div>
        {formError && <ErrorMessage message={formError} />}
        {variant !== "sales" ? (
          <ButtonsWrapper>
            {mode === "CHANGE_FULFILLMENT" && (
              <Link
                href={portalRoutes.mojeKonto.index.get({ mode: "ftth" })}
                passHref={true}
              >
                <A
                  onClick={handlePushDataLayer}
                  size={{ "@onlyMobile": "s" }}
                  variant="secondary"
                >
                  powrót do statusu
                </A>
              </Link>
            )}
            {mode === "RESERVE" && (
              <Link
                href={portalRoutes.mojeKonto.index.get({ mode: "ftth" })}
                passHref={true}
              >
                <A
                  onClick={handlePushDataLayer}
                  size={{ "@onlyMobile": "s" }}
                  variant="secondary"
                >
                  dokończ później
                </A>
              </Link>
            )}
            {mode === "CHANGE" && (
              <Button
                onPress={handleCancelAppointmentChange}
                size={{ "@onlyMobile": "s" }}
                variant="secondary"
              >
                anuluj
              </Button>
            )}
            <Button
              isLoading={!matches("ready")}
              size={{ "@onlyMobile": "s" }}
              type="submit"
            >
              wybierz
            </Button>
          </ButtonsWrapper>
        ) : (
          <>
            <ButtonsWrapper>
              {mode === "CHANGE" ? (
                <Link href={salesRoutes.podsumowanie.get()} passHref={true}>
                  <A size="inline" variant="simple">
                    <Text withUnderline={true}>anuluj</Text>
                  </A>
                </Link>
              ) : (
                <Button
                  display="inline"
                  onPress={modal.open}
                  size="inline"
                  variant="simple"
                >
                  <Text withUnderline={true}>anuluj</Text>
                </Button>
              )}
              <Button
                isLoading={!matches("ready")}
                size={{ "@onlyMobile": "s" }}
                type="submit"
              >
                wybierz
              </Button>
            </ButtonsWrapper>
            {modal.isOpened && (
              <Modal onClose={modal.close} title="anulowanie zamówienia">
                <CancelOrderModal onClose={modal.close} orchestratorId={id} />
              </Modal>
            )}
          </>
        )}
      </form>
    </>
  );
}
